/**
 * Referral-link capture for the V-coins program.
 *
 * Every coach has a unique referral link of the form
 *   https://<marketplace-host>/invite?ref=<coupon-code>
 * (the ?ref param is honored on ANY landing path, not just /invite — see
 * ReferralPage.getReferralLink() for where the link is generated, and
 * InvitePage for the landing page it points to).
 *
 * When a visitor arrives with ?ref=, we stash the code in localStorage
 * for 30 days. When they later subscribe, StripePayoutPage sends the
 * stored code to subscription-service's /checkout, which validates it
 * (existence + self-referral guard) and auto-applies the 20% discount
 * to the Chargebee Hosted Page. The referrer then mints V-coins via the
 * subscription webhook.
 *
 * We also fire a fire-and-forget "click" ping to subscription-service so
 * the referring coach can see how many people opened their link, not just
 * who converted (see ReferralPage.js's "Who you've invited" card). Deduped
 * per browser session (sessionStorage, not the 30-day localStorage above) so
 * navigating from /invite?ref=X to /signup?ref=X within the same visit
 * doesn't count as two opens — see subscription-service's
 * scripts/referralClicks.js for why a visitor reopening the link in a later
 * session is still counted again by design.
 */
import config from '../config';

const STORAGE_KEY = 'viveka_referral';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const CLICK_SESSION_KEY = 'viveka_referral_click_sent';

const trackReferralOpen = code => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    if (window.sessionStorage.getItem(CLICK_SESSION_KEY) === code) return;
    window.sessionStorage.setItem(CLICK_SESSION_KEY, code);
    fetch(`${config.subscriptionServiceUrl}referral/click`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // Best-effort — click tracking must never break the app.
  }
};

/**
 * Read ?ref= from the current URL and persist it. Call once on client
 * bootstrap. Safe to call in any environment (no-ops without window).
 */
export const captureReferralCode = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (code && /^[A-Za-z0-9_-]{4,64}$/.test(code)) {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ code, at: Date.now() })
      );
      trackReferralOpen(code);
    }
  } catch (e) {
    // localStorage can throw in private mode — referral capture is
    // best-effort, never break the app over it.
  }
};

/**
 * The stored referral code, or null if absent/expired.
 */
export const getReferralCode = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { code, at } = JSON.parse(raw);
    if (!code || !at || Date.now() - at > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return code;
  } catch (e) {
    return null;
  }
};

/** Clear the stored code (e.g. after a successful referred checkout). */
export const clearReferralCode = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};
